import { GetBuyerId } from '../../components/smartsend/item/GetBuyerId';
import { GetOrderReferencesStr } from '../../components/smartsend/item/OrderReferenceNumberUtils';
import { SmartSendItemGroup } from '../../components/smartsend/item/SmartSendGroup';
import { MarketplaceOrderApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { MarketplaceService } from '../consts/ItemMetaConstants';
import { SmartSendItemState } from '../context/SmartSendContextModels';
import { ApplierOption } from './ApplierOption';
import { onlyUnique, onlyUniqueNonBlank, SumNumber } from './ArrayHelper';
import { getDateGroupKey } from '~/lib/utils/array';
import { containsSearchTerm } from './containsSearchTerm';
import { FilterArea } from './DefaultFiltersPreferences';
import { SmartSendFilterCollator } from './SmartSendFilterCollator';
import { IsSmartSend } from './SmartSendSummaryGenerator';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

export function isInTransit(x: SmartSendItemApiModel) {
  if (!x.BookingDetails || !x.TrackingSummary || (!x.TrackingSummary?.DroppedOff && !x.TrackingSummary?.Collected)) {
    return false;
  }

  return (
    x.BookingDetails.BookingStatus === 'Booked' &&
    !x.TrackingSummary.Delivered &&
    ['AwaitingDispatch', 'Dispatched'].includes(x.MarketplaceStatus)
  );
}

export function getMarketplaceTransactionProcessedDate(x: SmartSendItemApiModel): Date {
  return getMarketplaceOrderTransactionProcessedDate(x.MarketplaceOrderLines[0]);
}

export function getMarketplaceOrderTransactionProcessedDate(x: MarketplaceOrderApiModel): Date {
  const d = x?.Meta['TransactionProcessed']?.Value;
  if (d && !isNaN(new Date(d).getTime())) {
    return new Date(d);
  }
  return null;
}

function getDateTimeKey(t: Date): number {
  const dt = new Date(t);
  return new Date(
    dt.getUTCFullYear(),
    dt.getUTCMonth(),
    dt.getUTCDate(),
    dt.getUTCHours(),
    dt.getUTCMinutes(),
    0,
    0
  ).getTime();
}

const NO_DATE = 'No Date';

export const SmartSendItemFilterApplier = (masterItems: SmartSendItemApiModel[], filter: SmartSendFilterContext) =>
  masterItems.slice().filter((x) => {
    if (filter.CurrentFilter.SearchTerm && !containsSearchTerm(x, filter.CurrentFilter.SearchTerm.toLowerCase())) {
      return false;
    }
    const hasMatch = filter.AvailableFilters.map((f) => {
      const s = filter.CurrentFilter.FilterSelections.find((x) => x.Name === f.name);
      return f.valid(x, f, s, x.State);
    });
    return !hasMatch.length || !hasMatch.includes(false);
  });

const SmartSendFilterApplier = (
  masterItems: SmartSendItemApiModel[],
  filter: SmartSendFilterContext
): SmartSendItemGroup[] => {
  const items = SmartSendItemFilterApplier(masterItems, filter);

  const headers = items
    .map((x) => {
      switch (filter.CurrentFilter.SortField) {
        case 'Date':
          return `DATE|CREATED:${getDateGroupKey(x.Created)}`;
        case 'TransactionProcessed':
          return `DATE|TRANSACTED:${
            (x.MarketplaceTransactionDate && getDateGroupKey(x.MarketplaceTransactionDate)) || NO_DATE
          }`;
        case 'Weight':
          return `Package Weight ${Number(x.PackageDimensions?.Weight).toFixed(2)}kg`;
        case 'Recipient':
          return x.PropertyKey.replaceAll('|', ' ');
        case 'Status':
          return x.Status;
        case 'MarketplaceReference':
          return GetOrderReferencesStr(x) || 'No Reference';
        case 'BuyerId':
          return GetBuyerId(x) || 'None';
        case 'TrackingStatus':
          return x.TrackingSummary?.LatestStatus;
        case 'MarketplaceStatus':
          return x.MarketplaceStatus;
        case 'Source':
          return x.Source.Name;
        case 'RecipientName':
          return x.Recipient.Name;
        case 'SKU':
          return (
            x.MarketplaceOrderLines.map((x) => x.Product?.Sku)
              .filter(onlyUniqueNonBlank)
              .sort()
              .join(', ') || ' (No SKU)'
          );
        case 'MarketplaceService':
          return (
            x.MarketplaceOrderLines.map((x) => x.Meta[MarketplaceService]?.Value)
              .filter(onlyUniqueNonBlank)
              .sort()
              .join(', ') || ' (No Service)'
          );
        case 'RecipientPostcode':
          return x.Recipient.Postcode || '-';
        case 'OrderQuantity':
          return `Quantity Ordered ${SumNumber(x.MarketplaceOrderLines.map((x) => x.Quantity))}`;
        case 'Service':
          return x.Service?.Name ?? 'No Service Selected';
        case 'Country':
          return x.Recipient?.CountryIsoCode;
        case 'Product':
          return x.Description;
        default:
          return x.Description;
      }
    })
    .filter(onlyUnique)
    .sort(SmartSendFilterCollator.compare);

  if (filter.CurrentFilter.SortOrder !== 'asc') {
    headers.reverse();
  }

  const output = headers
    .map((h) => {
      return {
        Title: h,
        Hidden: false,
        States: [],
        IsGroupable: filter.CurrentFilter.Name === 'Groupable',
        Items: items.filter((x) => {
          switch (filter.CurrentFilter.SortField) {
            case 'Date':
              return `DATE|CREATED:${getDateGroupKey(x.Created)}` === h;
            case 'TransactionProcessed':
              return (
                `DATE|TRANSACTED:${
                  (x.MarketplaceTransactionDate && getDateGroupKey(x.MarketplaceTransactionDate)) || NO_DATE
                }` === h
              );
            case 'Recipient':
              return x.PropertyKey.replaceAll('|', ' ') === h;
            case 'Status':
              return x.Status === h;
            case 'MarketplaceReference':
              return (GetOrderReferencesStr(x) || 'No Reference') === h;
            case 'BuyerId':
              return (GetBuyerId(x) || 'None') === h;
            case 'MarketplaceStatus':
              return x.MarketplaceStatus === h;
            case 'TrackingStatus':
              return x.TrackingSummary?.LatestStatus === h;
            case 'SKU':
              return (
                (x.MarketplaceOrderLines.map((x) => x.Product?.Sku)
                  .filter(onlyUniqueNonBlank)
                  .sort()
                  .join(', ') || ' (No SKU)') === h
              );
            case 'MarketplaceService':
              return (
                (x.MarketplaceOrderLines.map((x) => x.Meta[MarketplaceService]?.Value)
                  .filter(onlyUniqueNonBlank)
                  .sort()
                  .join(', ') || ' (No Service)') === h
              );
            case 'RecipientName':
              return x.Recipient.Name === h;
            case 'RecipientPostcode':
              return (x.Recipient.Postcode || '-') === h;
            case 'OrderQuantity':
              return `Quantity Ordered ${SumNumber(x.MarketplaceOrderLines.map((x) => x.Quantity))}` === h;
            case 'Service':
              return (x.Service?.Name ?? 'No Service Selected') === h;
            case 'Country':
              return x.Recipient?.CountryIsoCode === h;
            case 'Source':
              return x.Source.Name === h;
            case 'Product':
              return x.Description === h;
            case 'Weight':
              return `Package Weight ${Number(x.PackageDimensions?.Weight).toFixed(2)}kg` === h;
            default:
              return x.Description === h;
          }
        })
      };
    })
    .filter((x) => x.Items.length >= filter.CurrentFilter.MinGroupSize)
    .map((x) => {
      // Need to replace title as its not sortable as toString()
      if (x.Title.indexOf('DATE|') > -1) {
        x.Items = x.Items.sort((a, b) => {
          if (x.Title.indexOf('DATE|TRANSACTED:') > -1) {
            if (getDateTimeKey(a.MarketplaceTransactionDate) > getDateTimeKey(b.MarketplaceTransactionDate)) {
              return filter.CurrentFilter.SortOrder === 'asc' ? 1 : -1;
            }
            if (getDateTimeKey(a.MarketplaceTransactionDate) < getDateTimeKey(b.MarketplaceTransactionDate)) {
              return filter.CurrentFilter.SortOrder === 'asc' ? -1 : 1;
            }
          } else {
            if (getDateTimeKey(a.Created) > getDateTimeKey(b.Created)) {
              return filter.CurrentFilter.SortOrder === 'asc' ? 1 : -1;
            }
            if (getDateTimeKey(a.Created) < getDateTimeKey(b.Created)) {
              return filter.CurrentFilter.SortOrder === 'asc' ? -1 : 1;
            }
          }
        });
        x.Title = x.Title.indexOf(NO_DATE) > -1 ? NO_DATE : new Date(parseInt(x.Title.split(':')[1])).toDateString();
      }
      x.States = x.Items.filter(IsSmartSend)
        .map((i) => i.State?.state)
        .filter(onlyUnique);
      return x;
    });

  // Record the order
  let cnt = 0;
  for (let index = 0; index < output.length; index++) {
    for (let i = 0; i < output[index].Items.length; i++) {
      output[index].Items[i].State.order = cnt;
      cnt++;
    }
  }

  return output;
};

export type SortOrderField = 'asc' | 'desc';

export function TranslateSortField(name: SortField) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  switch (name) {
    case 'MarketplaceReference':
      return t('SORT_FIELD_MARKETPLACE_REFERENCE', { defaultValue: 'Marketplace Reference' });

    case 'BuyerId':
      return t('SORT_FIELD_RECIPIENT_EBAY_BUYID', { defaultValue: 'Recipient eBay BuyerId' });

    case 'MarketplaceStatus':
      return t('SORT_FIELD_MARKETPLACE_ORDER_STATUS', { defaultValue: 'Marketplace Order Status' });

    case 'Source':
      return t('SORT_FIELD_MARKETPLACE_NAME', { defaultValue: 'Marketplace Name' });

    case 'TransactionProcessed':
      return t('SORT_FIELD_MARKETPLACE_SOLD_DATE', { defaultValue: 'Marketplace Sold Date' });

    case 'Status':
      return t('SORT_FIELD_ITEM_STATUS', { defaultValue: 'Item Status (Active / Saved for Later)' });

    case 'Recipient':
      return t('SORT_FIELD_RECIPIENT_ADDRESS', { defaultValue: 'Recipient Address' });

    case 'OrderQuantity':
      return t('SORT_FIELD_ORDER_QUANTITY', { defaultValue: 'Item Quantity' });

    case 'Product':
      return t('SORT_FIELD_PRODUCT_NAME', { defaultValue: 'Product Name' });

    case 'SKU':
      return t('SORT_FIELD_PRODUCT_SKU', { defaultValue: 'Product SKU' });

    case 'Weight':
      return t('SORT_FIELD_ITEM_WEIGHT', { defaultValue: 'Item Weight' });

    case 'RecipientName':
      return t('SORT_FIELD_RECIPIENT_NAME', { defaultValue: 'Recipient Name' });

    case 'Country':
      return t('SORT_FIELD_RECIPIENT_COUNTRY', { defaultValue: 'Recipient Country' });

    case 'RecipientPostcode':
      return t('SORT_FIELD_RECIPIENT_POSTCODE', { defaultValue: 'Recipient Postcode' });

    case 'MarketplaceService':
      return t('SORT_FIELD_MARKETPLACE_SERVICE', { defaultValue: 'Marketplace Service' });

    case 'Date':
      return t('SORT_FIELD_IMPORT_DATE', { defaultValue: 'Item Imported / Created' });

    case 'Service':
      return t('SORT_FIELD_COURIER_SERVICE', { defaultValue: 'Item Courier Service' });

    default:
      return name;
  }
}

export type SortField =
  | 'MarketplaceStatus'
  | 'MarketplaceReference'
  | 'BuyerId'
  | 'Status'
  | 'TransactionProcessed'
  | 'Date'
  | 'Source'
  | 'Product'
  | 'Recipient'
  | 'RecipientName'
  | 'RecipientPostcode'
  | 'Service'
  | 'Country'
  | 'OrderQuantity'
  | 'Weight'
  | 'SKU'
  | 'MarketplaceService'
  | 'TrackingStatus';

export interface FilterOption {
  text: string;
  value: string;
  image?: string;
}

export interface FilterItem {
  valid: (i: SmartSendItemApiModel, f: FilterItem, s: FilterSelection, c: SmartSendItemState) => boolean;
  options: FilterOption[];
  name: string;
  label?: string;
  hidden?: boolean;
  style: 'DROP' | 'CHECKBOX' | 'ICONCHECKBOX' | 'IMAGE';
}

export interface FilterSelection {
  Include: string[];
  Exclude: string[];
  Name: string;
}

export interface DashStat {
  value: string | number;
  key: string;
}

export interface SmartSendFilterContext {
  AvailableFilters: FilterItem[];
  CurrentFilter: SmartSendFilter;
}

export type FilterName =
  | 'DEFAULT'
  | 'Sold'
  | 'Ready'
  | 'NotReady'
  | 'Issues'
  | 'InTransit'
  | 'Booked'
  | 'SavedForLater'
  | 'AwaitingDispatch'
  | 'Important'
  | 'Groupable'
  | 'Sold_Mobile'
  | 'Post_Create';

export interface SmartSendFilter {
  FilterSelections: FilterSelection[];
  SortField: SortField;
  SecondarySortField?: SortField;
  SearchTerm: string;
  SortOrder: SortOrderField;
  ShowAsGroup: boolean;
  DisableFilter?: boolean;
  DisableSort?: boolean;
  MinGroupSize: number;
  MaxGroupSize?: number;
  Name?: FilterName;
  Area?: FilterArea;
  Take: number;
  Skip: number;
}

export interface SmartSendSummary {
  count: number;
  ready: number;
  notReady: number;
  savedForLater: number;
  states: string[];
  awaitingDispatch: number;
  groupCount: number;
  selected: string[];
  bulkOptions: ApplierOption[];
  stats: DashStat[];
}

export function hasSold(filter: SmartSendFilter): boolean {
  return !!(
    !filter.FilterSelections.find((x) => x.Name === 'MarketplaceStatus') ||
    filter.FilterSelections.find(
      (x) => x.Name === 'MarketplaceStatus' && !x.Exclude.includes('Sold') && x.Include.includes('Sold')
    )
  );
}

export function checkArrayLists(s: FilterSelection, arr: string[]) {
  if (!s || (!s.Include.length && !s.Exclude.length)) {
    return true;
  }
  if (s.Include.length && !arr.find((t) => s.Include.includes(t))) {
    return false;
  }
  if (s.Exclude.length && !!arr.find((t) => s.Exclude.includes(t))) {
    return false;
  }
  return true;
}

export function checkLists(s: FilterSelection, val: string) {
  if (!s || (!s.Include.length && !s.Exclude.length)) {
    return true;
  }
  if (s.Include.length && !s.Include.includes(val)) {
    return false;
  }
  if (s.Exclude.length && s.Exclude.includes(val)) {
    return false;
  }
  return true;
}

export function DefaultSummary(): SmartSendSummary {
  return {
    stats: [],
    bulkOptions: [],
    count: 0,
    groupCount: 0,
    savedForLater: 0,
    selected: [],
    ready: 0,
    notReady: 0,
    awaitingDispatch: 0,
    states: []
  };
}

export default SmartSendFilterApplier;
